import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { MsfTreeSelect } from '@components/tree-select/tree-select';
import { ServerTranslateService } from '@services/server-translate.service';
import { map, startWith } from 'rxjs';

export interface RegionDictionarySelectItem {
  id: string;
  label: string;
}

@Component({
  selector: 'app-region-dictionary-select',
  standalone: true,
  imports: [TranslateModule, MsfTreeSelect],
  templateUrl: './region-dictionary-select.html',
  styleUrl: './region-dictionary-select.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegionDictionarySelectComponent {
  private readonly regionManager = inject(RegionManagerService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input('Regions');
  readonly model = input<readonly string[]>([]);
  readonly modelChange = output<string[]>();

  readonly regions = signal<Region[]>([]);
  readonly pendingId = signal<string | null>(null);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(
      map(event => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  readonly selectedRegions = computed<RegionDictionarySelectItem[]>(() => {
    this.lang();
    const byId = new Map<string, Region>();
    for (const region of this.regions()) {
      if (region.id) {
        byId.set(region.id, region);
      }
    }
    return this.model().map(id => ({
      id,
      label: this.labelFor(id, byId),
    }));
  });

  constructor() {
    this.regionManager.getRegionsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.regions.set(Array.isArray(data) ? data : data ? [data] : []);
      });
  }

  onPendingChange(id: string | null): void {
    if (id && !this.model().includes(id)) {
      this.modelChange.emit([...this.model(), id]);
    }
    this.pendingId.set(id);
    queueMicrotask(() => this.pendingId.set(null));
  }

  removeRegion(id: string): void {
    this.modelChange.emit(this.model().filter(item => item !== id));
  }

  private labelFor(id: string, byId: Map<string, Region>): string {
    const region = byId.get(id);
    if (!region) {
      return id;
    }
    return this.serverTranslate.getDisplayValue(region, 'names') || id;
  }
}
